import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react-lite';

import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import Dialog from '../../components/ui-controls/Dialog';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {cleanPhoneNumber} from '../../utils/phoneNumber';
import {rewardsMarketingName} from '../../utils/rewards';
import {PhoneNumberOnlyFormData, phoneNumberOnlySchema} from '../account/schemas/authSchemas';

import {AccountStatus} from './types';

// DIS-1384 R3/R19 shared canonical copy — byte-identical to
// LmgStaffSelectionScreen; the dash is a real em-dash (—), not a hyphen.
const INVALID_INVITE_COPY = 'Your invite link is invalid — request a new one.';

const RewardsSignInScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.REWARDS_SIGN_IN>) => {
    const {offersStore, rewardsStore} = useStores();

    // DIS-1228: params now typed via RootRewardsFlowParamList — query string
    // is parsed/normalised in linking.ts (decodeURIComponent → trim on
    // signUpCode). Case is deliberately PRESERVED (linking.ts:357), so the
    // DIS-1660 validation below is a case-sensitive match against the offer
    // code exactly as authored.
    const params = route.params;
    const isStaff = params?.staff === 'true';

    // DIS-1384 R1: the emailed LMG deep-link carries an opaque invite secret as
    // `lmgCode`. The linking parser injects it verbatim (decoded, NOT trimmed),
    // but the REWARDS_SIGN_IN param type doesn't yet declare it, so read via a
    // local cast rather than widening the shared navigationTypes here.
    const lmgCode = (params as {lmgCode?: string} | undefined)?.lmgCode;
    const hasLmgCode = typeof lmgCode === 'string' && lmgCode !== '';

    // R3: an explicit invalid invite (200 valid:false OR 403) blocks advancing;
    // any transient error fails open (submit-time 403 is the real gate, C2).
    const [inviteInvalid, setInviteInvalid] = useState(false);

    // DIS-1660 Decision 2: the verdict is DERIVED on every render, never frozen in
    // a mount-time effect. Reading these observables inside the observer render is
    // also what establishes the MobX subscription that lazily starts the offers
    // fetch (offersStore.ts:187 onBecomeObserved) and re-renders when it resolves.
    const urlSignUpCode = params?.signUpCode;
    const signUpOffers = offersStore.signUpOfferData;
    const isLoadingOffers = offersStore.isLoadingSignUpOffers;

    // Three states, spec Decision 4. Loading/absent/empty is UNVERIFIABLE, never
    // invalid: offersStore.ts:206 collapses a fetch failure to [], so an empty
    // list is not proof the code is unknown. Unverifiable fails OPEN.
    const signUpCodeVerdict: 'none' | 'unverifiable' | 'valid' | 'invalid' = !urlSignUpCode
      ? 'none'
      : isLoadingOffers || !signUpOffers || signUpOffers.length === 0
        ? 'unverifiable'
        : signUpOffers.some(offer => offer.signUpCode === urlSignUpCode)
          ? 'valid'
          : 'invalid';

    // The dismissal is keyed on the code that was rejected, never a bare boolean.
    // RootRewardsFlowStackNavigator.tsx:58 sets detachInactiveScreens={true},
    // which detaches the native view but does NOT unmount this screen, so a
    // boolean survives a params update and would swallow the dialog for a
    // SECOND, different invalid code arriving on the same screen instance.
    const [dismissedSignUpCode, setDismissedSignUpCode] = useState<string | null>(null);
    const invalidCodeDismissed = dismissedSignUpCode === urlSignUpCode;

    // DIS-1660 Decision 5: removal must clear BOTH the store and the route param.
    // A surviving route param would re-fire the dialog; clearing the store alone
    // fails the other way — rewardsStore.signInRouteParams re-echoes signUpCode
    // into the URL. Only the rejected code is cleared from the store: an
    // unrelated code rehydrated from the in-flight snapshot (rewardsStore.ts:202)
    // must not be wiped by dismissing a bad URL code.
    const handleInvalidSignUpCodeOk = () => {
      setDismissedSignUpCode(urlSignUpCode ?? null);
      if (!rewardsStore.signUpCode || rewardsStore.signUpCode === urlSignUpCode) {
        rewardsStore.setSignUpCode('');
      }
      navigation.setParams({signUpCode: undefined});
    };

    useEffect(() => {
      if (!hasLmgCode) {
        return;
      }
      // R1: enter LMG mode and store the raw code verbatim (no trim/parse).
      rewardsStore.setIsLmgStaffFlow(true);
      rewardsStore.setLmgStaffCode(lmgCode);
      // R26 (LANDMINE): the emailed link carries BOTH staff=true AND lmgCode.
      // lmgCode wins — force the store-staff flag false so the store picker and
      // STAFF pill never leak onto the LMG path.
      rewardsStore.setIsStaffSignup(false);

      let cancelled = false;
      (async () => {
        try {
          const res = await apiService.rewardsLmgStaffInvite({code: lmgCode});
          if (cancelled) {
            return;
          }
          // Advisory (C2): block only on an explicit valid:false; a missing/true
          // flag proceeds.
          if (res?.data?.valid === false) {
            setInviteInvalid(true);
          }
        } catch (e) {
          // FAIL-OPEN on any transient error (5xx / timeout / network). Only an
          // explicit 403 blocks.
          const err = e as {
            response?: {status?: number};
            cause?: {response?: {status?: number}};
            status?: number;
          };
          const status = err?.response?.status ?? err?.cause?.response?.status ?? err?.status;
          if (status === 403 && !cancelled) {
            setInviteInvalid(true);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lmgCode]);

    const {
      control,
      handleSubmit,
      formState: {errors, touchedFields, isSubmitting},
      setError,
    } = useForm<PhoneNumberOnlyFormData>({
      resolver: zodResolver(phoneNumberOnlySchema),
      defaultValues: {phoneNumber: ''},
      mode: 'onTouched',
    });

    const onSubmit = async (values: PhoneNumberOnlyFormData) => {
      // R3: an explicit invalid invite blocks advancing past the landing.
      if (inviteInvalid) {
        return;
      }

      const phoneNumber = cleanPhoneNumber(values.phoneNumber || '');

      try {
        const result = await apiService.rewardsCheckAccountExists({
          phoneNumber,
        });
        if (result?.status === 200) {
          rewardsStore.setPhoneNumber(phoneNumber);

          // Set signUpCode if provided in query parameters. DIS-1660: gated on the
          // CURRENT verdict, so a list that resolves between mount and submit is
          // honoured and a rejected code is never re-written.
          if (params?.signUpCode && signUpCodeVerdict !== 'invalid') {
            rewardsStore.setSignUpCode(params.signUpCode);
          }

          // DIS-1135 bug_002: set unconditionally so a non-staff sign-in on a
          // device that previously hosted a staff sign-in clears the persisted
          // flag. Otherwise routing falls into verifyStaffPhone (401) and
          // MemberCard renders STAFF badge for the wrong user.
          // DIS-1384 R26: on the LMG path lmgCode wins — never let staff=true set
          // the store-staff flag.
          rewardsStore.setIsStaffSignup(hasLmgCode ? false : isStaff);

          // DIS-1646: the DIS-1135 bug_002 clear above never got an LMG
          // counterpart when DIS-1384 added these flags. The LMG effect returns
          // early without an lmgCode, so a consumer sign-in on a device that
          // previously ran an LMG invite kept isLmgStaff true and a later
          // completion dump() persisted it onto the wrong account. Badge
          // staleness is prevented here, on flow entry, so a local true is only
          // ever set by the flow currently running.
          if (!hasLmgCode) {
            rewardsStore.setIsLmgStaffFlow(false);
            rewardsStore.setIsLmgStaff(false);
            rewardsStore.setLmgStaffCode('');
          }

          const accountStatus = result.data?.accountStatus;
          switch (accountStatus) {
            case AccountStatus.SIGNUP_LITE:
              // Brand new user — no Zoho or Zen account
              navigation.navigate(AppRoute.REWARDS_SIGN_UP, {
                flowType: AccountStatus.SIGNUP_LITE,
              });
              break;
            case AccountStatus.REWARDS_SIGNUP:
              // Exists in Zoho but not Zen — opt-in flow
              navigation.navigate(AppRoute.REWARDS_WELCOME_BACK, {
                flowType: AccountStatus.REWARDS_SIGNUP,
              });
              break;
            case AccountStatus.ACTIVATE_REWARDS:
              // Exists in both, card inactive — opt-in flow
              navigation.navigate(AppRoute.REWARDS_WELCOME_BACK, {
                flowType: AccountStatus.ACTIVATE_REWARDS,
              });
              break;
            case AccountStatus.NO_LITE_SIGNUP:
              if (isStaff) {
                // Staff signup for existing member — run through activate flow
                // to assign staff group on their existing card
                navigation.navigate(AppRoute.REWARDS_WELCOME_BACK, {
                  flowType: AccountStatus.ACTIVATE_REWARDS,
                });
              } else {
                // User has active rewards — verify phone → display card via Success screen.
                // Applies to both logged-in and non-logged-in users (e.g. Zen-only card holders).
                // Backend runs as lite-sign-in: verifies phone, returns existing Zen card data.
                navigation.navigate(AppRoute.REWARDS_WELCOME_BACK, {
                  flowType: AccountStatus.NO_LITE_SIGNUP,
                });
              }
              break;
            default:
              navigation.navigate(AppRoute.REWARDS_WELCOME_BACK, {
                flowType: AccountStatus.NO_SIGNUP,
              });
              break;
          }
        } else {
          setError('phoneNumber', {type: 'manual', message: 'Something went wrong'});
        }
      } catch (_e) {
        setError('phoneNumber', {
          type: 'manual',
          message: 'Could not check account. Please try again.',
        });
      }
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <RewardsProgramImage variant="onLight" />

          <View>
            <Text variant="heading1">{isStaff ? 'WELCOME STAFF MEMBERS!' : 'WELCOME!'}</Text>
          </View>
          <Text>
            This is the first step to unlocking {isStaff ? 'Free staff' : 'FREE member'} benefits by
            creating your {rewardsMarketingName} account.
            {'\n\n'}
            Let&apos;s start with your mobile number.
          </Text>

          <Controller
            control={control}
            name="phoneNumber"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                containerStyle={{marginTop: 36}}
                onChangeText={(text: string) => {
                  // ios safari autofill doesn't include country code
                  // when autofill number starting with +61 so add it here
                  if (text.length === 9 && text?.[0] === '4') {
                    text = '+61' + text;
                  }
                  onChange(text);
                }}
                value={value || ''}
                accessibilityLabel="Enter your mobile number"
                autoCapitalize="none"
                autoComplete="tel"
                errorMessageWhenTouched={errors.phoneNumber?.message}
                keyboardType="phone-pad"
                label="Enter your mobile number"
                maxLength={20}
                onBlur={onBlur}
                touched={!!touchedFields.phoneNumber}
              />
            )}
          />
          {inviteInvalid ? <Text style={{marginTop: 16}}>{INVALID_INVITE_COPY}</Text> : null}
          <View style={{marginTop: 16}}>
            <Button
              title={'CONTINUE'}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting || inviteInvalid}
              loading={isSubmitting}
            />
          </View>
        </ContentView>

        {/* DIS-1660: Alert.alert is a no-op on react-native-web, and a
            registration deep link is a web URL — the popup must be an in-app
            modal. Dialog with cancelVisible={false} and no onCancel gives a
            single OK button and an inert backdrop. */}
        {signUpCodeVerdict === 'invalid' && !invalidCodeDismissed ? (
          <Dialog
            isVisible
            cancelVisible={false}
            okTestID="signup-code-invalid-ok"
            onOk={handleInvalidSignUpCodeOk}
            title="The sign up code is not valid. Click OK to continue to remove."
          />
        ) : null}
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsSignInScreen;
